// eBay's taxonomy declares which values of one aspect may be paired with another — the Size
// "XL" is valid only with Size Types "Maternity", "Regular" and "Juniors". The backend
// (Ebay::AspectConstraints) normalises those into both directions, so every value carries the
// constraints that apply to it and filtering is a purely local decision here.

export interface ValueConstraint {
  applicableForName: string
  applicableForValues: string[]
}

export interface ConstrainedValue {
  value: string
  valueConstraints?: ValueConstraint[]
}

export type SiblingValues = Record<string, unknown>

const normalize = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()

const isSet = (value: unknown): boolean => normalize(value) !== ''

/**
 * The aspect a user must fill in before this one can be offered, or null when nothing blocks it.
 * Size Type is meaningless until a Size is chosen, so the form gates it rather than showing a
 * list the user would have to guess their way through.
 */
export const gatingAspect = (constrainedBy: string[] | undefined, siblingValues: SiblingValues): string | null =>
  (constrainedBy || []).find(name => !isSet(siblingValues?.[name])) ?? null

/**
 * Narrows a value list to those compatible with the sibling values already chosen.
 *
 * A constraint is only applied when the sibling value is one the constraint data knows about.
 * Free-text sizes such as "32x33" appear in no constraint list, and filtering on them would
 * empty the dropdown and strand the user; eBay rejects that size on its own terms instead.
 */
export const filterByConstraints = <T extends ConstrainedValue>(values: T[], siblingValues: SiblingValues): T[] => {
  // Every sibling value this list knows how to reason about. A value outside it (free text the
  // user typed) carries no compatibility information, so constraints naming it are skipped.
  const knownFor = (aspectName: string): Set<string> => {
    const known = new Set<string>()
    values.forEach(entry => {
      ;(entry.valueConstraints || []).forEach(constraint => {
        if (constraint.applicableForName !== aspectName) return
        ;(constraint.applicableForValues || []).forEach(allowed => known.add(normalize(allowed)))
      })
    })
    return known
  }

  const recognised = new Map<string, Set<string>>()

  return values.filter(entry => {
    const constraints = entry.valueConstraints || []
    if (constraints.length === 0) return true

    return constraints.every(constraint => {
      const sibling = siblingValues?.[constraint.applicableForName]
      if (!isSet(sibling)) return true

      if (!recognised.has(constraint.applicableForName)) {
        recognised.set(constraint.applicableForName, knownFor(constraint.applicableForName))
      }
      if (!recognised.get(constraint.applicableForName).has(normalize(sibling))) return true

      return (constraint.applicableForValues || []).some(allowed => normalize(allowed) === normalize(sibling))
    })
  })
}
